import { NumberFormatter } from "@mantine/core"
import { useMemo } from "react"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { StaffApi } from "../../apis"
import { IAuth } from "../../store/Auth"
import { renderFullName } from "../../utils/render-name"
import type { Tone } from "../delivery/atoms"

/** COD reconciliation status → dot tone (BE: DRAFT -> SETTLED | DISPUTED | WRITTEN_OFF). */
export const COD_STATUS_TONE: Record<string, Tone> = {
  DRAFT: "amber",
  SETTLED: "green",
  DISPUTED: "red",
  WRITTEN_OFF: "grey",
}

export function Money({ value }: Readonly<{ value: number }>) {
  return <NumberFormatter value={value} thousandSeparator="," decimalScale={2} fixedDecimalScale suffix=" ₮" />
}

/**
 * Driver name by id from /ord/staff/select (the COD rows carry only driverUserId). O104: never the
 * UUID; an unknown driver shows «-».
 */
export function useDriverNames() {
  const businessId = useSelector((s: { auth: IAuth }) => s.auth.user?.currentBusinessId ?? null)
  const { data } = useSWR(
    businessId ? ["cod.drivers", businessId] : null,
    () => StaffApi.select({ businessId: businessId as string, limit: 200 }),
    { revalidateOnFocus: false },
  )
  return useMemo(() => {
    const map = new Map<string, string>()
    for (const s of data ?? []) map.set(s.id, renderFullName(s) || "-")
    return (id: string | null | undefined) => (id ? map.get(id) ?? "-" : "-")
  }, [data])
}
